import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Row, { RowBetween } from '@/components/Row';
import { AutoColumn } from '@/components/Column';
import Button from '@/components/Button';
import TokenList from '@/components/Tokenlist';
import AddToken from '@/components/AddTokenToWallet';
import TooltipInfo from '@/components/TooltipInfo';
import { TYPE } from '@/theme';
import BigNumber from 'bignumber.js';
import { Token, Tokens, NETWORK_CONFIG } from '../../../constants';
import { isEthereum, isMetisChain } from '@/utils';
import { BridgeStrategy } from '../types';
import ICPlusSVG from '@/assets/images/icon_+.svg';
import ICMinsSVG from '@/assets/images/icon_-.svg';
import DateSVG from '@/assets/images/date.svg';
import DropdownIcon from '@/assets/images/svg/dropdown.svg?react';
import moment from 'moment';

const StyledTokenInfoRow = styled(RowBetween)`
    width: 100%;
    height: 72px;
    box-sizing: border-box;
    padding: 8px 20px;
    border-radius: 20px;
    cursor: pointer;
    background: ${({ theme }) => theme.c.bg04};

    ${({ theme }) => theme.mediaWidth.upToSmall`
    height: 60px;

    .balance {
        font-size: 12px !important;
    }
    `}
`;

const TokenListButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    box-sizing: border-box;
    height: 56px;
    border-radius: 20px;
    cursor: pointer;
    gap: 8px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
    height:  48px;
    `}
    img {
        &.logo {
            width: 28px;
            height: 28px;
            margin-right: 8px;
            border-radius: 14px;
        }
    }
`;

const StyledArrowDropDownIcon = styled(DropdownIcon)`
    width: 12px;
    height: 8px;
    color: #00cfff;
    margin-left: 8px;

    .loadingIcon {
        font-size: 12px !important;
        color: #fff !important;
    }
`;

const MaxButton = styled(Button)`
    width: 64px;
    height: 36px;
    background: #07ddd0;
    border-radius: 15px !important;
    color: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px !important;
    box-shadow: 3px 7px 15px -3px rgba(0, 158, 169, 0.3) !important;
    flex-shrink: 0;
`;

const InputRow = styled(Row)`
    width: 100%;
    margin-top: 24px;
    height: 100px;
    align-items: center;
    box-sizing: border-box;
    padding: 24px;
    border-radius: 24px;
    background: ${({ theme }) => theme.c.bg04};
    justify-content: space-between;
    ${({ theme }) => theme.mediaWidth.upToSmall`
   
    height: 75px;
    padding: 8px 16px;
    margin-top: 8px;
    `}
`;

const InputField = styled.input`
    background: transparent;
    flex-grow: 1;
    max-width: 310px;
    height: 30px;
    outline: none;
    border: none;
    color: ${({ theme }) => theme.c.t01};
    font-size: 36px;
    font-weight: bold;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    &::placeholder {
        color: ${({ theme }) => theme.c.t02};
    }

    ${({ theme }) => theme.mediaWidth.upToSmall`
     width: calc(100% - 70px);
   
    `};
`;

const SendIcon = styled.div`
    width: 20px;
    height: 20px;
    margin-right: 8px;
    background-position: center center;
    background-size: 100%;
    background-repeat: no-repeat;

    &.plus {
        background-image: url(${ICPlusSVG});
    }
    &.delete {
        background-image: url(${ICMinsSVG});
    }
`;

const ReceivingAddressWrapper = styled.div`
    width: 100%;
    background: ${({ theme }) => theme.c.bg04};
    border-radius: 15px;
    margin-top: 20px;
    box-sizing: border-box;
    padding: 16px;
    .receivingAddressInput {
        background: transparent;
        height: 30px;
        outline: none;
        border: none;
        color: ${({ theme }) => theme.c.t01};
        font-size: 14px;
        font-weight: bold;
        width: 100%;

        &::placeholder {
            color: ${({ theme }) => theme.c.t02};
        }
    }
`;

const GasFeeWrapper = styled(RowBetween)`
    width: 100%;
    border: 2px solid #00cfff;
    padding: 10px 14px;
    box-sizing: border-box;
    border-radius: 24px;
    background-color: rgba(0, 207, 255, 0.1);
`;

const DateIcon = styled.div`
    width: 15px;
    height: 15px;
    margin-right: 8px;
    background: url(${DateSVG}) 100% center no-repeat;
`;

const ConfirmButton = styled(Button)`
    width: 280px;
    margin-top: 24px;
    font-size: 18px !important;
    box-shadow: none !important;
    ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
    margin-top: 12px;
    `}
`;

interface BridgeTokenProps {
    strategy: BridgeStrategy;
    connected: boolean;
    walletAddress: string;
    onConnect: () => void;
}

export default function BridgeToken({
    strategy,
    connected,
    walletAddress,
    onConnect,
}: BridgeTokenProps) {
    const [input, setInput] = useState('');
    const [isOpenTokenList, setIsOpenTokenList] = useState(false);
    const [showSend, setShowSend] = useState(false);
    const [receivingAddress, setReceivingAddress] = useState('');

    const {
        currentToken,
        setCurrentToken,
        currentTokenBalance,
        inputAmount,
        setInputAmount,
        inputErrorMessage,
        approved,
        approveToken,
        transfer,
        transFeeInfo,
        loadingTransFee,
        handleMax,
        getLeftAmount,
        fromChainId,
        toChainId,
        bridgeConfig,
        estimateFee,
    } = strategy;

    const tokenInfo = (Tokens[currentToken] as any)[fromChainId];
    const isGasToken =
        (isEthereum(fromChainId) && currentToken === Token.eth) ||
        (isMetisChain(fromChainId) && currentToken === Token.metis);

    // Sync input with inputAmount
    useEffect(() => {
        if (!new BigNumber(input || 0).isEqualTo(inputAmount)) {
            setInput(inputAmount.toNumber() ? inputAmount.toFixed() : '');
        }
    }, [inputAmount, input]);

    // Set receiving address to wallet address on mount
    useEffect(() => {
        if (walletAddress) {
            setReceivingAddress(walletAddress);
        }
    }, [walletAddress]);

    // Reset input on chain change
    useEffect(() => {
        setInputAmount(new BigNumber(0));
    }, [fromChainId, setInputAmount]);

    const nextDisabled =
        !receivingAddress || !inputAmount.toNumber() || !!inputErrorMessage;

    async function handleNext() {
        if (!connected) {
            onConnect();
        } else {
            if (approved) {
                await transfer(receivingAddress);
            } else {
                await approveToken();
            }
        }
    }

    function handleDestinationAddressOnChange(
        e: React.ChangeEvent<HTMLInputElement>,
    ) {
        setReceivingAddress(e.currentTarget.value);
    }

    async function handleMaxFunc() {
        await handleMax(receivingAddress);
    }

    function formatGasNum(fee: number) {
        if (!approved || fee === -1 || fee === 0) {
            return '-';
        }
        if (fee < 1e-6) {
            return '< 0.000001';
        } else {
            const feeString = `${fee}`;
            if (feeString.length > 6) {
                return new BigNumber(fee).toFixed(6);
            }
            return new BigNumber(fee).toFixed();
        }
    }

    useEffect(() => {
        if (
            !inputErrorMessage &&
            approved &&
            receivingAddress &&
            inputAmount.isGreaterThan(0)
        ) {
            estimateFee(receivingAddress, inputAmount);
        }
    }, [inputAmount, inputErrorMessage, receivingAddress, approved]);

    if (!tokenInfo) return null;

    return (
        <>
            <StyledTokenInfoRow
                onClick={() => {
                    setIsOpenTokenList(true);
                }}
            >
                <TokenListButtonWrapper>
                    <img src={tokenInfo.img} alt="" className={'logo'} />
                    <TYPE.main fontSize={14} fontWeight={600}>
                        {tokenInfo.symbol}
                    </TYPE.main>
                    <StyledArrowDropDownIcon />
                </TokenListButtonWrapper>
                <TYPE.main className="balance" fontSize={14} color="#A2A2A8">
                    Balance: &nbsp;&nbsp;
                    {tokenInfo
                        ? Number(currentTokenBalance.toFixed(6))
                        : '0'}{' '}
                    {tokenInfo.symbol}
                </TYPE.main>
            </StyledTokenInfoRow>

            <InputRow align="between">
                <InputField
                    type="text"
                    placeholder="0.00"
                    min={0}
                    value={input}
                    onChange={(e) => {
                        const inputValue = e.currentTarget.value.replace(
                            /^\D*([0-9]\d*\.?\d*)?.*$/,
                            '$1',
                        );

                        setInput(inputValue);
                        setInputAmount(new BigNumber(`${inputValue}` || 0));
                    }}
                />
                <MaxButton
                    onClick={handleMaxFunc}
                    loadingProps={{
                        classname: 'loading',
                        size: 16,
                    }}
                    showLoading
                >
                    MAX
                </MaxButton>
            </InputRow>

            {!isGasToken && connected && (
                <Row align="between">
                    <span></span>
                    <AddToken token={currentToken} />
                </Row>
            )}

            {inputErrorMessage && (
                <Row>
                    <TYPE.main fontSize={14} color="red">
                        {inputErrorMessage}
                    </TYPE.main>
                </Row>
            )}

            {connected && (
                <>
                    <AutoColumn
                        justify="center"
                        gap="md"
                        style={{ marginTop: '12px' }}
                    >
                        <TYPE.main
                            color="t03"
                            fontSize={16}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: ' pointer',
                            }}
                            onClick={() => {
                                if (showSend && connected && walletAddress) {
                                    setReceivingAddress(walletAddress);
                                }
                                setShowSend(!showSend);
                            }}
                        >
                            <SendIcon
                                className={showSend ? 'delete' : 'plus'}
                            />
                            Send to
                        </TYPE.main>
                        {showSend && (
                            <ReceivingAddressWrapper>
                                <Row lineHeight="none">
                                    <TYPE.desc
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        Receiving address
                                        <TooltipInfo text="Please watch out that wrong ERC20 address will not be accepted." />
                                    </TYPE.desc>
                                </Row>
                                <Row>
                                    <input
                                        type="text"
                                        autoFocus={showSend}
                                        value={receivingAddress}
                                        className={'receivingAddressInput'}
                                        onChange={
                                            handleDestinationAddressOnChange
                                        }
                                    />
                                </Row>
                            </ReceivingAddressWrapper>
                        )}
                        <RowBetween>
                            <TYPE.main
                                fontSize={12}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                Estimated time of Arrival
                                <TooltipInfo
                                    text={
                                        <TYPE.main fontSize={12} color="#fff">
                                            {bridgeConfig.etaTooltip}
                                        </TYPE.main>
                                    }
                                />
                            </TYPE.main>
                            <TYPE.main
                                fontSize={12}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                Estimated Fee
                                <TooltipInfo
                                    text={
                                        <AutoColumn>
                                            <TYPE.main
                                                fontSize={12}
                                                color="#fff"
                                            >
                                                Estimated Gas Fee:{' '}
                                                {loadingTransFee
                                                    ? 'Calculating'
                                                    : `${formatGasNum(
                                                          transFeeInfo
                                                              ? transFeeInfo.fee_num
                                                              : -1,
                                                      )} ${
                                                          bridgeConfig.gasTokenName
                                                      }`}
                                            </TYPE.main>
                                            {bridgeConfig.showProtocolFee && (
                                                <AutoColumn>
                                                    <TYPE.main
                                                        fontSize={12}
                                                        color="#fff"
                                                    >
                                                        The Protocol Fee:{' '}
                                                        {loadingTransFee
                                                            ? 'Calculating'
                                                            : `${formatGasNum(
                                                                  transFeeInfo
                                                                      ? transFeeInfo.servicesFee
                                                                      : -1,
                                                              )} ${
                                                                  bridgeConfig.gasTokenName
                                                              }`}
                                                    </TYPE.main>
                                                    <TYPE.main
                                                        fontSize={12}
                                                        color="#fff"
                                                    >
                                                        {
                                                            'The gas cost could change from time to time. Please review before you confirm the transaction. Protocol Fee is paid to withdraw tokens to Ethereum Mainnet.'
                                                        }
                                                    </TYPE.main>
                                                </AutoColumn>
                                            )}
                                            {!bridgeConfig.showProtocolFee && (
                                                <TYPE.main
                                                    fontSize={12}
                                                    color="#fff"
                                                >
                                                    {
                                                        'The gas cost could change from time to time. Please review before you confirm the transaction.'
                                                    }
                                                </TYPE.main>
                                            )}
                                        </AutoColumn>
                                    }
                                />
                            </TYPE.main>
                        </RowBetween>
                        <GasFeeWrapper>
                            <AutoColumn gap="4px" style={{ width: '100%' }}>
                                <RowBetween>
                                    <TYPE.main fontSize={12}>
                                        {`${NETWORK_CONFIG[fromChainId].name} - ${NETWORK_CONFIG[toChainId].name}`}
                                    </TYPE.main>
                                </RowBetween>

                                <RowBetween style={{ width: '100%' }}>
                                    <TYPE.main
                                        fontSize={24}
                                        fontWeight={600}
                                        mobileFz={16}
                                    >
                                        {bridgeConfig.timeDisplay}
                                    </TYPE.main>
                                    <TYPE.main
                                        fontSize={24}
                                        fontWeight={600}
                                        mobileFz={16}
                                    >
                                        {loadingTransFee
                                            ? 'Calculating'
                                            : `${
                                                  bridgeConfig.showProtocolFee
                                                      ? formatGasNum(
                                                            transFeeInfo
                                                                ? transFeeInfo.servicesFee
                                                                : -1,
                                                        )
                                                      : formatGasNum(
                                                            transFeeInfo
                                                                ? transFeeInfo.fee_num
                                                                : -1,
                                                        )
                                              } ${bridgeConfig.gasTokenName}`}
                                    </TYPE.main>
                                </RowBetween>
                                {bridgeConfig.showDate && (
                                    <RowBetween>
                                        <TYPE.main
                                            fontSize={12}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <DateIcon />
                                            Before&nbsp;
                                            {moment()
                                                .add(8, 'days')
                                                .format('MMM DD, YYYY (UTC Z)')}
                                        </TYPE.main>
                                        <TYPE.main fontSize={12}>
                                            + GAS FEE
                                        </TYPE.main>
                                    </RowBetween>
                                )}
                            </AutoColumn>
                        </GasFeeWrapper>
                    </AutoColumn>
                </>
            )}

            <Row justify="center">
                <ConfirmButton
                    onClick={handleNext}
                    disabled={connected && nextDisabled}
                    showLoading
                >
                    {connected
                        ? approved
                            ? 'Confirm'
                            : 'Approve'
                        : 'Connect Wallet'}
                </ConfirmButton>
            </Row>

            <TokenList
                isOpen={isOpenTokenList}
                chainId={fromChainId}
                toChainId={toChainId}
                onClose={() => {
                    setIsOpenTokenList(false);
                }}
                onSelectToken={(name: Token) => {
                    setCurrentToken(name);
                }}
            />
        </>
    );
}
